import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Cell, Column, Row } from 'react-table';
import MaterialIcon from 'commons/src/components/MaterialIcon';
import { PartnerOfflineDevicesResponse } from '../../../actions/partnerActions';
import { BuildingSummaryHealthStatus } from '../../../models/buildingModels';
import HealthStatusTable, {
    CustomerRowData,
    LocationRowData,
} from '../../status/buildingsStatus/components/DesktopTable/HealthStatusTable';
import styles from './PartnerDesktopTable.module.scss';
import RedirectToDashboardButton from './RedirectToDashboardButton';

type ParentProps = {
    data: PartnerOfflineDevicesResponse[];
    numberOfDevicesInOrg: number;
    numberOfCustomers: number;
};

type RowData = {
    userGroupId: string;
    customerName: string;
    totalLocations: number;
    hubs: number;
    sensorDevices: number;
    buildings: number;
    offlineLocations: BuildingSummaryHealthStatus[];
    display: {
        buildings: string;
        sensorDevices: string;
        hubs: string;
    };
};

const DisplayValue = ({ column, cell }: { column: Column; cell: Cell }): React.ReactElement => (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <span>{column.accessor(cell.row.original.display)}</span>
);

const toDisplayFraction = (nominator: number, denominator: number): string =>
    nominator === 0 ? '0' : `${nominator}/${denominator}`;

const toPercentage = (nominator: number, denominator: number): number =>
    nominator === 0 ? 0 : nominator / denominator;

const toRowData = ({
    userGroupId,
    customerName,
    totalLocations,
    offlineLocations,
}: PartnerOfflineDevicesResponse): RowData => {
    const totalOfflineHubs = offlineLocations.map(location => location.offlineHubs).reduce((acc, val) => acc + val, 0);
    const totalHubs = offlineLocations.map(location => location.hubs).reduce((acc, val) => acc + val, 0);
    const totalOfflineSensorDevices = offlineLocations
        .map(location => location.offlineSensorDevices)
        .reduce((acc, val) => acc + val, 0);
    const totalSensorDevices = offlineLocations
        .map(location => location.sensorDevices)
        .reduce((acc, val) => acc + val, 0);
    return {
        userGroupId,
        customerName,
        totalLocations,
        offlineLocations,
        buildings: toPercentage(offlineLocations.length, totalLocations),
        hubs: toPercentage(totalOfflineHubs, totalHubs),
        sensorDevices: toPercentage(totalOfflineSensorDevices, totalSensorDevices),
        display: {
            buildings: toDisplayFraction(offlineLocations.length, totalLocations),
            hubs: toDisplayFraction(totalOfflineHubs, totalHubs),
            sensorDevices: toDisplayFraction(totalOfflineSensorDevices, totalSensorDevices),
        },
    };
};

const ExpandRow = ({
    row,
    cell,
}: {
    row: Row<LocationRowData | CustomerRowData>;
    cell: Cell<LocationRowData | CustomerRowData>;
}): React.ReactElement => {
    const rowData = row.original as RowData;
    const hasOfflineLocations = rowData.offlineLocations && rowData.offlineLocations.length > 0;

    if (!hasOfflineLocations) {
        return <span className={styles.alignedCellContent}>{cell.value}</span>;
    }

    return (
        <span className={styles.alignedCellContent}>
            <span {...row.getToggleRowExpandedProps()} className={styles.expandTableElement}>
                <MaterialIcon name={row.isExpanded ? 'keyboard_arrow_down' : 'keyboard_arrow_right'} />
            </span>
            {cell.value}
        </span>
    );
};

const TypedDisplayValue = ({
    column,
    cell,
}: {
    column: Column<LocationRowData | CustomerRowData>;
    cell: Cell<LocationRowData | CustomerRowData>;
}): React.ReactElement => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return <DisplayValue column={column} cell={cell} />;
};

const RedirectToDashboard = ({ cell }: { cell: Cell<LocationRowData | CustomerRowData> }): React.ReactElement => (
    <RedirectToDashboardButton userGroupId={cell.value as string} />
);

type Props = ParentProps;

export default function PartnerDesktopTable({
    data,
    numberOfDevicesInOrg,
    numberOfCustomers,
}: Props): React.ReactElement {
    const { t: txt } = useTranslation();
    const numberOfOfflineData = data.length;
    const columns = useMemo<Column<LocationRowData | CustomerRowData>[]>(
        () =>
            [
                {
                    Header: `${txt('Partner.Customer')} (${numberOfOfflineData}/${numberOfCustomers})`,
                    accessor: 'customerName' as keyof (LocationRowData | CustomerRowData),
                    className: styles.textCell,
                    Cell: ExpandRow,
                },
                {
                    Header: txt('BuildingStatus.OfflineBuildingsHeader'),
                    accessor: 'buildings' as keyof (LocationRowData | CustomerRowData),
                    Cell: TypedDisplayValue,
                    className: styles.fractionCell,
                },
                {
                    Header: txt('BuildingStatus.OfflineHubsHeader'),
                    accessor: 'hubs',
                    Cell: TypedDisplayValue,
                    className: styles.fractionCell,
                },
                {
                    Header: txt('BuildingStatus.OfflineSensorDevicesHeader'),
                    accessor: 'sensorDevices',
                    Cell: TypedDisplayValue,
                    className: styles.fractionCell,
                },
                {
                    Header: '',
                    accessor: 'userGroupId' as keyof (LocationRowData | CustomerRowData),
                    Cell: RedirectToDashboard,
                    className: styles.partnerButtonCell,
                },
            ] as Column<LocationRowData | CustomerRowData>[],
        [txt, numberOfOfflineData, numberOfCustomers]
    );

    const dataSorted = useMemo(
        () =>
            data.sort(
                (a, b) => b.offlineLocations.length / b.totalLocations - a.offlineLocations.length / a.totalLocations
            ),
        [data]
    );
    const tableData = useMemo<RowData[]>(() => dataSorted.map(toRowData), [dataSorted]);

    const emptyTableText = (): string => {
        if (numberOfDevicesInOrg > 0) return 'BuildingStatus.NoOfflineBuildings';
        if (data.length === 0) return 'BuildingStatus.NoCustomers';
        return 'BuildingStatus.NoDevicesInBuildings';
    };

    return <HealthStatusTable data={tableData} columns={columns} styles={styles} emptyTableText={emptyTableText()} />;
}
